import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import Lottie from "lottie-react";
import successAnimation from "../assets/success confetti.json";
import failedAnimation from "../assets/failed.json";
import pendingAnimation from "../assets/pending.json";
import { useParams } from "react-router-dom";
import axios from "axios";
import { BASE_URL, IMG_URL } from "../config/config";
import { useAuth } from "../context/AuthContext";

function ConfirmOrder() {
  const navigate = useNavigate();
  const { order_id } = useParams();
  const orderId = order_id || Math.floor(100000 + Math.random() * 900000);

  const { setIsLoggedIn } = useAuth();

  const [orderData, setOrderData] = useState(null);
  const [error, setError] = useState("");

  const [countdown, setCountdown] = useState(10);

  useEffect(() => {
    // Send orderId to backend on page load
    const sendOrderId = async () => {
        try {
            const response = await axios.post(
            `${BASE_URL}/getPurchaseDetails`,
            { order_id: orderId },
            {
                headers: {
                "Content-Type": "application/json",
                },
            }
            );

            // Add finalAmount to every product
            response.data.purchase_History.products =
                response.data.purchase_History.products.map((product) => {
                    const priceData = JSON.parse(product.price);

                    return {
                    ...product,
                    price: priceData[0]?.finalAmount || 0,
                    };
                });

            setOrderData(response.data);

        } catch (err) {
            console.error(err);
            setError("Failed to confirm order. Please try again.");
        }
        };

    sendOrderId();
  }, [orderId]);


  useEffect(() => {
  if (!orderData) return;

  const interval = setInterval(() => {
    setCountdown((prev) => {
      if (prev <= 1) {
        clearInterval(interval);

        sessionStorage.clear();
        setIsLoggedIn(false);

        navigate("/", { replace: true });

        return 0;
      }

      return prev - 1;
    });
  }, 1000);

  return () => clearInterval(interval);
}, [orderData, navigate]);

  const status = orderData?.purchase_History?.transaction_status?.toUpperCase() || "PENDING";


  const statusConfig = {
    SUCCESS: {
      title: "Order Confirmed 🎉",
      message: "Your order has been placed successfully.",
      animation: successAnimation,
    },
    FAILED: {
      title: "Payment Failed ❌",
      message: "Your payment could not be processed.",
      animation: failedAnimation,
    },
    PENDING: {
      title: "Payment Pending ⏳",
      message: "Your payment is under processing.",
      animation: pendingAnimation,
    },
  };

  const currentStatus = statusConfig[status] || statusConfig.PENDING;

  if (error) {
    return (
      <div style={styles.loaderWrapper}>
        <p style={{ color: "red" }}>{error}</p>
      </div>
    );
  }


  const courseImage = orderData?.purchase_History.products[0].image
      ? `${IMG_URL}/uploads/${encodeURIComponent(orderData?.purchase_History.products[0].image)}`
      : "/placeholder-course.png";

  return (
    <div style={styles.page}>
      <style>{`
        @keyframes fadeIn {
          from { opacity: 0; transform: translateY(20px);}
          to { opacity: 1; transform: translateY(0);}
        }
        .btn-hover:hover {
          transform: translateY(-3px);
          box-shadow: 0 10px 25px rgba(255,90,79,0.3);
        }
        .support-card:hover { transform: translateY(-6px); }
        .product-hover:hover { transform: scale(1.02); }
      `}</style>

      <div className="container">

        {/* TOP CARD */}
        <div style={styles.topCard}>
          <div style={{ width: 180, margin: "0 auto" }}>
            <Lottie animationData={currentStatus.animation} loop={false} />
          </div>

          <h1 style={styles.heading}>{currentStatus.title}</h1>

          <p style={styles.subText}>
            {currentStatus.message}
          </p>

          {/* Redirect Countdown */}
          <div
            style={{
              marginTop: "24px",
              padding: "12px 20px",
              display: "inline-flex",
              alignItems: "center",
              gap: "8px",
              background: "#f8f9ff",
              border: "1px solid #e8ebff",
              borderRadius: "12px",
              color: "#555",
              fontSize: "15px",
              fontWeight: 500,
            }}
          >
            <span>🏠 Redirecting to Home in</span>
            <span
              style={{
                minWidth: "32px",
                textAlign: "center",
                background: "#ff5a4f",
                color: "#fff",
                borderRadius: "8px",
                padding: "4px 10px",
                fontWeight: 700,
                fontSize: "16px",
              }}
            >
              {countdown}
            </span>
            <span>sec</span>
          </div>
        </div>

        {/* ORDER DETAILS */}
        <div style={styles.card}>
          <h3 style={styles.cardTitle}>Order Details</h3>
          <p style={{ marginBottom: "20px" }}>
            Confirmation No: <span style={styles.highlight}>#{orderId}</span>
          </p>

          <div style={styles.productCard} className="product-hover">
            <img
              src={courseImage}
              alt={courseImage}
              style={styles.productImg}
            />
            <div style={{ flex: 1 }}>
              <p style={styles.productTitle}>Item Purchase: {orderData?.purchase_History.products[0].product_name}</p>
              <small style={{ color: "#777" }}>Qty: {orderData?.purchase_History.products[0].quantity}</small>
            </div>
            <strong style={{ fontSize: "18px" }}>₹ {orderData?.purchase_History.products[0].price}</strong>
          </div>

          <div className="row mt-4">
            <div className="col-md-6">
              <h6 style={styles.sectionTitle}>Billing Address</h6>
              <p className="mb-0">{orderData?.purchase_History.billing_details.city}</p>
              <p className="mb-0">{orderData?.purchase_History.billing_details.Address}, {orderData?.purchase_History.billing_details.country}</p>
            </div>
            <div className="col-md-6">
              <h6 style={styles.sectionTitle}>Payment Summary</h6>
              <p style={{ marginBottom: "10px" }}>UPI</p>
              <Row label="Subtotal" value={`₹ ${orderData?.purchase_History.products[0].price * orderData?.purchase_History.products[0].quantity}`} />
              <Row label="Tax" value="₹ 0" />
              <hr />
              <Row label="Total" value={`₹ ${orderData?.purchase_History.products[0].price * orderData?.purchase_History.products[0].quantity}`} bold />
            </div>
          </div>
        </div>

        {/* CTA */}
        <div style={styles.cta}>
          <h3>Continue Shopping 🛍️</h3>
          <p>Discover more products and offers.</p>
          <button style={styles.ctaBtn} className="btn-hover" onClick={() => navigate("/")}>
            Shop Now
          </button>
        </div>

      </div>
    </div>
  );
}

/* 🔹 COMPONENTS */

const Status = ({ icon, label, active }) => (
  <div style={{ textAlign: "center", opacity: active ? 1 : 0.4 }}>
    <div
      style={{
        background: active ? "#ffe5e2" : "#eee",
        padding: "14px",
        borderRadius: "50%",
        display: "inline-block"
      }}
    >
      {React.cloneElement(icon, {
        size: 22,
        color: active ? "#ff5a4f" : "#999"
      })}
    </div>
    <p style={{ marginTop: "8px", fontSize: "14px" }}>{label}</p>
  </div>
);

const Line = () => (
  <div
    style={{
      width: "60px",
      height: "3px",
      background: "#ddd",
      borderRadius: "10px"
    }}
  />
);

const Row = ({ label, value, bold }) => (
  <div
    style={{
      display: "flex",
      justifyContent: "space-between",
      fontWeight: bold ? "600" : "400",
      marginBottom: "6px"
    }}
  >
    <span>{label}</span>
    <span>{value}</span>
  </div>
);

/* 🔹 STYLES */

const styles = {
  page: {
    background: "linear-gradient(135deg, #fff1ef, #f9f6f2)",
    minHeight: "100vh",
    padding: "50px 0",
    fontFamily: "Poppins, sans-serif"
  },

  topCard: {
    background: "rgba(255,255,255,0.9)",
    backdropFilter: "blur(12px)",
    borderRadius: "25px",
    padding: "50px 20px",
    textAlign: "center",
    marginBottom: "30px",
    boxShadow: "0 15px 40px rgba(0,0,0,0.08)",
    animation: "fadeIn 0.6s ease"
  },

  heading: {
    fontWeight: "700",
    fontSize: "32px"
  },

  subText: {
    color: "#666",
    marginBottom: "30px"
  },

  statusWrapper: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    gap: "25px",
    marginBottom: "30px",
    flexWrap: "wrap"
  },

  card: {
    background: "#fff",
    borderRadius: "20px",
    padding: "30px",
    marginBottom: "30px",
    boxShadow: "0 10px 30px rgba(0,0,0,0.06)",
    animation: "fadeIn 0.8s ease"
  },

  cardTitle: {
    fontWeight: "700",
    marginBottom: "15px",
    fontSize: "20px"
  },

  highlight: {
    color: "#ff5a4f",
    fontWeight: "600"
  },

  productCard: {
    display: "flex",
    alignItems: "center",
    gap: "15px",
    padding: "18px",
    borderRadius: "15px",
    background: "#fafafa",
    transition: "0.3s"
  },

  productImg: {
    borderRadius: "12px",
    width: "90px",
    height: "90px",
    objectFit: "cover"
  },

  productTitle: {
    fontWeight: "600"
  },

  sectionTitle: {
    fontWeight: "600",
    marginBottom: "10px"
  },

  supportCard: {
    background: "#fff",
    padding: "20px",
    borderRadius: "15px",
    textAlign: "center",
    boxShadow: "0 5px 15px rgba(0,0,0,0.06)",
    cursor: "pointer",
    transition: "all 0.3s ease"
  },

  cta: {
    background: "linear-gradient(135deg, #ff5a4f, #ff7a6f)",
    borderRadius: "25px",
    padding: "40px",
    textAlign: "center",
    marginTop: "30px",
    color: "#fff",
    boxShadow: "0 15px 40px rgba(255,90,79,0.3)"
  },

  primaryBtn: {
    background: "#ff5a4f",
    color: "#fff",
    borderRadius: "30px",
    padding: "14px 30px",
    border: "none",
    fontWeight: "600",
    cursor: "pointer",
    transition: "0.3s"
  },

  ctaBtn: {
    background: "#fff",
    color: "#ff5a4f",
    borderRadius: "30px",
    padding: "14px 30px",
    border: "none",
    fontWeight: "600",
    cursor: "pointer",
    marginTop: "10px"
  }
};

export default ConfirmOrder;